/**
 * Ask Aligned Context
 * 
 * Provides global access to the Ask Aligned command palette.
 * Handles the Cmd/Ctrl+K keyboard shortcut globally.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import AskAlignedPalette from "../components/ask-aligned/AskAlignedPalette";

const AskAlignedContext = createContext(null);

export function AskAlignedProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);

  // Open the palette
  const openAskAligned = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Close the palette
  const closeAskAligned = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Toggle the palette
  const toggleAskAligned = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // Global keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        toggleAskAligned();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [toggleAskAligned]);

  const value = {
    isOpen,
    openAskAligned,
    closeAskAligned,
    toggleAskAligned
  };

  return (
    <AskAlignedContext.Provider value={value}>
      {children}
      <AskAlignedPalette isOpen={isOpen} onClose={closeAskAligned} />
    </AskAlignedContext.Provider>
  );
}

// Hook to use Ask Aligned
export function useAskAligned() {
  const context = useContext(AskAlignedContext);
  if (!context) {
    throw new Error("useAskAligned must be used within AskAlignedProvider");
  }
  return context;
}

export default AskAlignedContext;
